<script>
    /*
        ServiceList.svelte

        07/20/22 - used for both Public and Auth searches

        Contains the ListHeader and ListItems for search results.
        The ListItems are the index records, not the full Service records.
        Search object passed in as the store prop and list is updated after each search.

        This is the JSON for each ServiceIndex returned:

        {
            average-rating: 0                       // new 10/12
            distance: 0.3254803374804409
            distanceUnit: "mi"
            exclusive-requirements: []
            postal-code: "63101"
            score: 1
            service-description: "NA - Migration"
            service-id: "663510"
            service-location: {lat: 38.6346, lon: -90.1913}
            service-name: "Gateway Region YMCA Downtown at MX"
            taxonomies: {
                open-eligibility-2022: ["health", "exercise & fitness", "care", "medical care", "medical care"]
                tcare-taxonomy-1: ["healthy lifestyle classes", "education/classes"]        
            }
        }

        08/02 - now using BaseServiceListItem components

    */

    import { onMount } from 'svelte';
    import { getSelectedItems, getSelectedIds, sortArrayItems } from '../../common/utils.js';
    import { addServicesToCollection, flagDuplicateReviews2 } from '../../api/services.ts';
    import { setInfoAlert, setErrorAlert, PENDING_MSECS } from '../../stores/AlertsStore';

    // TCARE components
	import ListHeader from './ListHeader.svelte';
	import ListActionBar from './ListActionBar.svelte';
    import AuthServiceListItem from './AuthServiceListItem.svelte';
    
    // SMUI components - mostly for Add To Collection Dialog
    import Dialog, { Content, Actions } from '@smui/dialog';
    import Button, { Label } from '@smui/button';    

    // 5/11 - NEW ARCHITECTURE
    export let store;                                           // Search object
    export let onAddToCollection;                               // called by List
    
    // cannot export these from a component! use lists.js?
    const CELL_CHECKBOX = 'cell-checkbox';
    const CELL_STANDARD = 'cell-standard';
    const CELL_SORTABLE = 'cell-sortable';
    const CELL_DIVIDER  = 'cell-divider';

    const FAKE_USER_ID = '12345';
    const BG_PATH = 'img/tcare_symbol.png';

    const FLAG_DUPS_PENDING = 'Flagging as duplicate services - click to cancel';
    const FLAG_DUPS_ERROR = 'Error while flagging as duplicate services';
    const FLAG_DUPS_SUCCESS = 'Successfully flagged as duplicate services';
    
    $: sortedResults = $store.postalResults;
    $: selectedIds = getSelectedIds(sortedResults);
    $: resultsMessage = getResultsMessage(sortedResults);
    $: selectedCount = 0;
    $: isAllSelected = false;

    let selectedCollObj = null;     // user selected from dialog drop-down list
    let newCollName = null;         // user typed into dialog input box
    let isSaveToCollDialogOpen = false;
    let userCollections = [];

    onMount(() => {
        setSelectedCount();

        let list = document.getElementById('search-list');
        if (list) {
            list.scrollTop = 100;
        }
    })

    const sortItems = (propName, isAscending) => {
        sortArrayItems(sortedResults, propName, isAscending);       // utils.js
        sortedResults = sortedResults;                              // must include this to refresh list
    }

    const setSelectedCount = () => {
        selectedCount = 0;
        if (!sortedResults) return;
        for (const result of sortedResults) {
            if (result.isSelected) selectedCount++;
        }
    }

    const selectAllItems = (val) => {
        for (const result of sortedResults) {
            result.isSelected = val;
        }
        setSelectedCount();
        sortedResults = sortedResults;          // Svelte refresh
    }

    const getResultsMessage = (sortedResults) => {
        if (!sortedResults) return '';
        if (!sortedResults.length) return '';
        if (sortedResults.length == 0) {
            return '';
        }
        if (sortedResults.length > 0 && sortedResults.some(sr => sr.score > 0)) {
            return 'Showing ' + sortedResults.length + ' results';
        }
        return 'No direct results found - showing ' + sortedResults.length + ' nearby services';
    }



    // header
    const onHeaderCheckboxClick = () => {
        isAllSelected = !isAllSelected;
        selectAllItems(isAllSelected);
    }

    // handler for ListItem checkbox
    const onItemCheckboxClick = (item) => {
        //currentItem = item;
        item.isSelected = !item.isSelected;
        setSelectedCount();
        // console.log('onItemCheckboxClick item.isSelected', item.isSelected);
        sortedResults = sortedResults;         // Svelte refresh - not working!
    }


    const headerCells = [
        {
            type: CELL_CHECKBOX,
            // type: CELL_STANDARD, 
            // label: '', 
            onCheckboxClick: onHeaderCheckboxClick,
            align: 'none',
            style: 'width: 5%;'
        },
        {   type: CELL_DIVIDER, align: '', style: '' },
        {
            type: CELL_STANDARD,            // spacer cell
            label: '',
            align: 'left',
            style: 'width: 4%;'
        },
        {
            type: CELL_SORTABLE, 
            label: 'Name', 
            propName: 'service-name', 
            onSort: sortItems,
            align: 'left',
            style: 'width: 60%;'
        },
        {
            type: CELL_DIVIDER,
            align: '',
            style: ''
        },
        {
            type: CELL_SORTABLE,
            label: 'ZIP Code',
            propName: 'postal-code',
            onSort: sortItems,
            align: 'center',
            style: 'width: 11%;'
        },
        {
            type: CELL_DIVIDER,
            align: '',
            style: ''
        },
        {
            type: CELL_SORTABLE, 
            label: 'Distance', 
            propName: 'distance', 
            onSort: sortItems,
            align: 'center',
            style: 'width: 10%;'
        },
        {
            type: CELL_DIVIDER,
            align: '',
            style: ''
        },
        // {
        //     type: CELL_STANDARD,
        //     label: 'Save',
        //     align: 'center',
        //     style: 'width: 5%;'
        // }
        {
            type: CELL_SORTABLE, 
            label: 'Rating', 
            propName: 'average-rating', 
            onSort: sortItems,
            align: 'center', 
            style: 'width: 10%;'
        }
    ]

    // Actions for the <ListActionBar> component 

    const flagDupes = async () => {
        const selItems = getSelectedItems(sortedResults);
        selectedIds = getSelectedIds(sortedResults);
        const selectedIntIds = selectedIds.map(id => parseInt(id));
        
        // passing in the function - will not run if Alert is cancelled
        setInfoAlert(FLAG_DUPS_PENDING, PENDING_MSECS, async () => {
            const json = await flagDuplicateReviews2(selectedIntIds);
            if (!json.errors) {
                setInfoAlert(FLAG_DUPS_SUCCESS, 2000);
                // pop();
                // TODO: refresh list here?
            } else {
                setErrorAlert(FLAG_DUPS_ERROR, 2000);         // based on API success
            }
            selectAllItems(false);
        });
    }
    

    /*
    // launches the dialog and fetches collection choices
    // clear out selections each time
    const addToColl = async () => {
        selectedCollObj = null;
        newCollName = null;
        sortedResults = sortedResults;          // Svelte refresh
        userCollections = await fetchUserCollections(FAKE_USER_ID);
        isSaveToCollDialogOpen = true;
    }
    */

    /*
    // not an action yet
    const removeSvc = () => {
        console.log('running CommonSearchResultsList removeSvc');
        console.log(getSelectedItems(sortedResults));
    }
    */
    
    const actions = [
        {action: flagDupes, label: 'Flag as Duplicate', roles: ['submitter']},
        // {action: addToColl, label: 'Add to Collection', roles: ['submitter']},
        // {action: removeSvc, label: 'Remove', roles: ['submitter']}
    ]

    
    // called after Dialog "Add" is closed (not cancelled)
    // services.ts API call
    const addServicesToColl = async () => {
        let name = '';
        if (newCollName) {
            name = newCollName;
        } else if (selectedCollObj) {
            name = selectedCollObj.name;
        }
        if (name !== '') {
            let json = await addServicesToCollection(FAKE_USER_ID, selectedIds, name);
            // show feedback on host Search Page if successful
            if (onAddToCollection) onAddToCollection(json);
        }
    }
    
</script>

<style>

    .common-search-results-list {
        scroll-behavior: smooth;
    }

    .showing-results {
        width: 100%;
        margin: 10px 0px;
        font-size: 15px;
        font-weight: 800;
        color: var(--tcare-primary);
    }

    .title-row {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    .title-icon {
        width: 30px;
        height: 24px;
    }

    .title-icon img {
        width: 100%;
        height: 100%;
    }

    .title-name {
        margin-left: 10px;
        font-size: 1.5em;
        font-weight: bold;
        color: var(--tcare-primary);
    }

    :global(.dialog-colls.mdc-dialog .mdc-dialog__surface) {
        width: 500px;
    }

    :global(.select-colls.mdc-select) {
        width: 100%;
        min-height: 200px;
    }    


    /* non SMUI controls */
    
    :global(.dialog-colls .select-colls) {
        width: 100%;
        padding: 5px;
        font-size: 1.1em;
    }    

    :global(.dialog-colls .input-coll) {
        width: 97%;
        padding: 5px;
        font-size: 1.1em;
    }    

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }
</style>


<!-- note that we are iterating over sortedResults here -->

<div id="search-list" class="common-search-results-list">
    <div class="showing-results">{resultsMessage}</div>

    {#if sortedResults && sortedResults.length > 0}
        <ListHeader {headerCells} />
        <ListActionBar {actions} {selectedCount} />
        {#each sortedResults as item, index}
            <!-- embed a scroll marker -->
            <AuthServiceListItem
                {item} 
                {index} 
                onCheckboxClick={onItemCheckboxClick} 
                isCheckbox={true}
            />
        {/each}
    {/if}
</div>





<!-- AddToCollection Dialog - redesign this! separate component -->
<Dialog class="dialog-colls" bind:open={isSaveToCollDialogOpen}>
    <Content>
        <div class="title-row ai-center jc-start">
            <div class="title-icon"><img src={BG_PATH} alt={'TCARE Symbol'}></div>
            <div class="title-name">Add to Collection</div>
        </div>
    </Content>

    <Content>
        Choose from your current collections:
        <select class="select-colls" bind:value={selectedCollObj}>
            {#each userCollections as collObj }
                <option value={collObj}>{collObj.name}</option>
            {/each}
        </select>
    </Content>
    <Content>
        Or enter a new collection:
        <br/>
        <input class="input-coll" bind:value={newCollName}>
    </Content>

    <Actions>
        <Button>
            <Label>Cancel</Label>
        </Button>
        {#if selectedCollObj || newCollName}
            <Button on:click={() => addServicesToColl()}>
                <Label>Add</Label>
            </Button>
        {/if}
    </Actions>
</Dialog>
